import { AvailabilityApi, PartnerApi, PropertyApi, BookingApi, PriceApi } from '@/services/api.ts';
import { getAvailabilityBulk, getBookingsQuantityBulk, getPricesBulk } from '@/services/bulkApi.ts';
import Partner from '@/models/Partner.ts';
import Property from '@/models/Property.ts';
import Room from '@/models/Room.ts';
import * as Moment from 'moment';
import {DateRange, extendMoment} from 'moment-range';
import Availability from '@/models/Availability';
import ApiError from '@/errors/ApiError';
import ServerError from '@/errors/ServerError';
import {
  UI_DAY_CELL_WIDTH,
  UI_CAPTION_COLUMN_WIDTH,
  UI_DEFAULT_DAYS_VIEW,
  UI_EXTENDED_MAX_DAYS_VIEW,
} from '@/utils/constants';
import { handleErrorResponse } from '@/utils/utils';
import {DropdownOptions} from '@/plugins/dropdown';
import partner from '@/stores/partner';

const moment = extendMoment(Moment);

// Groups a flat bulk-endpoint response (one row per room per date) into per-room arrays, keyed by
// the field each bulk endpoint uses to identify the room (`room` for prices/availability, `roomId`
// for booking quantities - see bb8-api's internal Price/Availability preparators vs. its raw booking
// quantity SQL).
function groupByRoomField(items: any[], field: string): Record<string, any[]> {
  const grouped: Record<string, any[]> = {};
  items.forEach((item: any) => {
    const roomId = item[field];
    if (!grouped[roomId]) {
      grouped[roomId] = [];
    }
    grouped[roomId].push(item);
  });
  return grouped;
}

export default {
  state: {
    partnerId: undefined,
    propertyList: [],
    roomList: [],
    selectedPropertyTitle: '',
    selectedRoom: undefined,
    dateRange: {
      start: moment().format('YYYY-MM-DD'),
      end: moment().add(6, 'd').format('YYYY-MM-DD'),
    },
    dropdownRoomStatus: {
      show: false,
    },
    isLoading: true,
    isFetching: false,
    isOnRequest: false,
    hasVariablePricing: false,
    windowWidth: window.innerWidth,
    transitionType: 'slide-left',
  },

  mutations: {
    setPartnerId(state: any, id: string) {
      state.partnerId = id;
    },

    setProperties(state: any, payload: Property[]) {
      state.propertyList = payload;
      state.selectedPropertyTitle = payload[0] && payload[0].name ? payload[0].name : '';
    },

    setOnRequest(state: any, payload: boolean) {
      state.isOnRequest = payload;
    },

    setHasVariablePricing(state: any, payload: boolean) {
      state.hasVariablePricing = payload;
    },

    setPropertyDetails(state: any, {id, detail}: any) {
      state.propertyList = [
        {
          ...detail,
        },
        ...state.propertyList.filter((property: any) => property.id !== id),
      ];
    },

    setRoomDetails(state: any, {id, detail}: any) {
      state.roomList = [detail, ...state.roomList.filter((room: any) => room.id !== id)];
    },

    updateAvailabilityDetail(state: any, {detail, roomId}: any) {
      const room = state.roomList.find((r: Room) => {
        return r.id === roomId;
      });
      if (!room) {
        return;
      }
      detail.forEach((availability: any) => {
        const aIndex = room.availabilities.findIndex((a: Availability) => {
          return a.date === availability.date;
        });
        if (aIndex !== -1) {
          room.availabilities[aIndex].availabilityType = availability.availabilityType;
          room.availabilities[aIndex].availabilityQty = availability.availabilityQty;
        } else {
          room.availabilities.push(availability);
        }
      });
    },

    updateBookingDetail(state: any, {detail, roomId}: any) {
      const room = state.roomList.find((r: Room) => {
        return r.id === roomId;
      });
      if (!room) {
        return;
      }
      detail.forEach((booking: any) => {
        const bIndex = room.bookings.findIndex((p: any) => {
          return p.date === booking.date;
        });
        if (bIndex !== -1) {
          room.bookings[bIndex].quantity = booking.quantity;
        } else {
          room.bookings.push(booking);
        }
      });
    },

    updatePriceDetail(state: any, {detail, roomId}: any) {
      const room = state.roomList.find((r: Room) => {
        return r.id === roomId;
      });
      if (!room) {
        return;
      }
      detail.forEach((price: any) => {
        const pIndex = room.prices.findIndex((p: any) => {
          return p.date === price.date;
        });
        if (pIndex !== -1) {
          room.prices[pIndex].amount = price.amount;
        } else {
          room.prices.push(price);
        }
      });
    },

    setSelectedRoom(state: any, room: Room) {
      state.selectedRoom = room;
    },

    updateDateRange(state: any, {start, end}: any) {
      state.transitionType = moment(state.dateRange.start).isBefore(start) ? 'slide-left' : 'slide-right';
      state.dateRange = {start, end};
    },

    setDropdownRoomStatus(state: any, value: DropdownOptions) {
      state.dropdownRoomStatus = value;
    },

    setLoadingState(state: any, value: boolean) {
      state.isLoading = value;
    },

    setFetchingState(state: any, value: boolean) {
      if (value) {
        state.isFetching = true;
      } else {
        window.setTimeout(() => {
          state.isFetching = false;
        }, 1000); // 1000 ms is the slide transition time
      }
    },

    setWindowWidth(state: any, value: number) {
      state.windowWidth = value;
    },
  },

  actions: {
    getPartnerDetails({ commit, state, dispatch }: any) {
      return new Promise((resolve, reject) => {
        PartnerApi.getPartnerItem(state.partnerId, (error: Error, data: Partner, response: Response) => {
          if (!error && typeof(data) !== 'undefined') {
            const { properties, externalId, id }: any = data;
            if (!properties || !properties.length) {
              reject(new ApiError('112'));
            } else {
              commit('setProperties', properties);
              commit('setHasVariablePricing', data.hasVariablePricing);
              return Promise.all(properties.map((property: any) => (
                dispatch('getRoomsFromProperty', {
                  propertyId: property.id,
                }))),
              ).then(() => resolve()).catch((e) => reject(e));
            }
          } else {
            if (typeof(response) !== 'undefined') {
              const errorMap: any = {
                404: '111',
              };
              reject(new ApiError(errorMap[response.status] || '110'));
            } else {
              reject(new ServerError('110'));
            }
          }
        });
      });
    },

    getVariablePricingDatesCurrentAndNextYear({ commit, state, dispatch }: any) {
      return new Promise((resolve, reject) => {
        PartnerApi.partnerVariablePricingDatesPartnerCollection(state.partnerId, {}, (
          error: Error, data: any, response: Response,
        ) => {
          if (!error && response.body) {
            resolve(response.body);
          } else {
            if (typeof(response) !== 'undefined') {
              const errorMap: any = {
                404: '111',
              };
              reject(new ApiError(errorMap[response.status] || '110'));
            } else {
              reject(new ServerError('110'));
            }
          }
        });
      });
    },

    getVariablePricingDates({commit, state, dispatch}: any, payload: any) {
      commit('setFetchingState', true);
      return new Promise((resolve, reject) => {
        const promises = payload.rooms.map((roomId: string) => {
          return dispatch('getPricesFromRoom', {
            startDate: moment().format('YYYY-MM-DD'),
            endDate: moment(new Date(payload.year, 12, 31)).format('YYYY-MM-DD'),
            roomId,
          });
        });
        Promise.all(promises)
          .then((responses: any) => {
            resolve(responses);
          }).catch((error: Error) => {
            reject(error);
          }).finally(() => {
            commit('setFetchingState', false);
          });
      });
    },

    getRoomsFromProperty({ commit, state, rootState, dispatch }: any, opts: any) {
      return new Promise((resolve, reject) => {
        PropertyApi.getPropertyItem(opts.propertyId, (error: Error, resData: any, response: any) => {
          if (!error && typeof(response) !== 'undefined') {
            commit('setPropertyDetails', { id: opts.propertyId, detail: response.body });
            if (response.body.rooms && response.body.rooms.length) {
              const rooms: Room[] = response.body.rooms;
              const isOnRequest: boolean = rooms.some(({availabilityType}: any) => (availabilityType === 'R'));
              commit('setOnRequest', isOnRequest);

              const pricingEnabled = process.env.VUE_APP_PRICING_ENABLED !== '0' && response.body.pricingEnabled;
              const isRoomPriceEligible = (room: Room) => (room.sellable === 'enabled' || state.hasVariablePricing);

              const roomIds = rooms.map((room: Room) => room.id);
              const priceEligibleRoomIds = pricingEnabled
                ? rooms.filter(isRoomPriceEligible).map((room: Room) => room.id)
                : [];

              const startDate = state.dateRange.start;
              const endDate = state.dateRange.end;

              // One bulk call per data type covers every room on the property, instead of the
              // previous per-room fan-out (N rooms x up to 3 requests fired concurrently), which is
              // what caused bb8-api PHP-FPM workers to be OOM-killed under load (AS-2587).
              return Promise.all([
                dispatch('getBulkAvailability', { roomIds, startDate, endDate }).catch(() => []),
                dispatch('getBulkBookingsQuantity', { roomIds, startDate, endDate }).catch(() => []),
                priceEligibleRoomIds.length
                  ? dispatch('getBulkPrices', { roomIds: priceEligibleRoomIds, startDate, endDate }).catch(() => [])
                  : Promise.resolve([]),
              ]).then(([availabilities, bookings, prices]: any) => {
                const availabilitiesByRoom = groupByRoomField(availabilities, 'room');
                const bookingsByRoom = groupByRoomField(bookings, 'roomId');
                const pricesByRoom = groupByRoomField(prices, 'room');

                rooms.forEach((room: Room) => {
                  commit('setRoomDetails', {
                    id: room.id,
                    detail: {
                      ...room,
                      availabilities: availabilitiesByRoom[room.id] || [],
                      bookings: bookingsByRoom[room.id] || [],
                      prices: pricingEnabled ? (pricesByRoom[room.id] || []) : [],
                      pricingEnabled: pricingEnabled && isRoomPriceEligible(room),
                      currencyCode: response.body.currencyCode,
                    },
                  });
                });
              }).finally(() => resolve());
            } else {
              reject(new ApiError('202'));
            }
          } else {
            if (typeof(response) !== 'undefined') {
              const errorMap: any = {
                404: '201',
              };
              reject(new ApiError(errorMap[response.status] || '200'));
            } else {
              reject(new ServerError('200'));
            }
          }
        });
      });
    },

    getDashboardData({ commit, dispatch, state }: any, dateRange: DateRange) {
      const dateList = Array
        .from(moment.range(dateRange.start, dateRange.end)
        .by('days'))
        .map((m) => m.format('YYYY-MM-DD'));

      const roomsNeedingRefresh: Room[] = state.roomList.filter((room: Room) => {
        return dateList.some((date) => (
          room.availabilities.findIndex((availability: any) => availability.date === date) === -1
          || room.bookings.findIndex((booking: any) => booking.date === date) === -1
          || (room.pricingEnabled && room.prices.findIndex((price: any) => price.date === date) === -1)
        ));
      });

      if (!roomsNeedingRefresh.length) {
        commit('updateDateRange', dateRange);
        return Promise.resolve();
      }

      commit('setFetchingState', true);

      const roomIds = roomsNeedingRefresh.map((room: Room) => room.id);
      const priceEligibleRoomIds = roomsNeedingRefresh
        .filter((room: Room) => room.pricingEnabled)
        .map((room: Room) => room.id);

      // Same batching as getRoomsFromProperty: one bulk call per data type for every room whose
      // visible date range grew, instead of one dispatch per room.
      return Promise.all([
        dispatch('getBulkAvailability', { roomIds, startDate: dateRange.start, endDate: dateRange.end })
          .catch(() => []),
        dispatch('getBulkBookingsQuantity', { roomIds, startDate: dateRange.start, endDate: dateRange.end })
          .catch(() => []),
        priceEligibleRoomIds.length
          ? dispatch('getBulkPrices', {
            roomIds: priceEligibleRoomIds, startDate: dateRange.start, endDate: dateRange.end,
          }).catch(() => [])
          : Promise.resolve([]),
      ]).then(([availabilities, bookings, prices]: any) => {
        const availabilitiesByRoom = groupByRoomField(availabilities, 'room');
        const bookingsByRoom = groupByRoomField(bookings, 'roomId');
        const pricesByRoom = groupByRoomField(prices, 'room');

        roomsNeedingRefresh.forEach((room: Room) => {
          const availabilityArray = availabilitiesByRoom[room.id] || [];
          const bookingArray = bookingsByRoom[room.id] || [];
          const priceArray = room.pricingEnabled ? (pricesByRoom[room.id] || []) : [];

          if (!availabilityArray.length || !bookingArray.length || (room.pricingEnabled && !priceArray.length)) {
            return;
          }
          if (availabilityArray.length !== bookingArray.length
            || (room.pricingEnabled && priceArray.length !== availabilityArray.length)) {
            return;
          }

          commit('updateAvailabilityDetail', {roomId: room.id, detail: availabilityArray});
          commit('updateBookingDetail', {roomId: room.id, detail: bookingArray});
          if (room.pricingEnabled) {
            commit('updatePriceDetail', {roomId: room.id, detail: priceArray});
          }
        });
      }).finally(() => {
        commit('setFetchingState', false);
        commit('updateDateRange', dateRange);
      });
    },

    getAvailabilityFromRoom({ commit, state, dispatch }: any, opts: any) {
      return new Promise((resolve, reject) => {
        AvailabilityApi.getAvailabilityAvailabilityCollection(
          opts.startDate,
          opts.endDate,
          { roomId: opts.roomId },
          (error: Error, resData: any, response: any) => {
            if (!error && typeof(response) !== 'undefined') {
              resolve(response.body);
            } else {
              if (typeof(response) !== 'undefined') {
                reject(new ApiError('300'));
              } else {
                reject(new ServerError('300'));
              }
              reject(error);
            }
          });
        });
    },

    getBookingFromRoom({commit, state, dispatch}: any, opts: any) {
      return new Promise((resolve, reject) => {
        BookingApi.getBookingsQuantityBookingCollection(
          opts.startDate,
          opts.endDate,
          {
            roomId: opts.roomId,
            bookingStatus: 'committed',
          },
          (error: Error, resData: any, response: any) => {
            if (!error && typeof(response) !== 'undefined') {
              resolve(response.body);
            } else {
              if (typeof(response) !== 'undefined') {
                reject(new ApiError('400'));
              } else {
                reject(new ServerError('400'));
              }
            }
          });
        });
    },

    getPricesFromRoom({commit, state, dispatch}: any, opts: any) {
      return new Promise((resolve, reject) => {
        PriceApi.getPricesPriceCollection(opts.startDate, opts.endDate, {roomId: opts.roomId},
          (error: Error, data: any, response: any) => {
          if (!error && typeof(response) !== 'undefined') {
            resolve(response.body);
          } else {
            if (typeof(response) !== 'undefined') {
              reject(new ApiError('510'));
            } else {
              reject(new ServerError('510'));
            }
          }
        });
      });
    },

    // Bulk (multi-room) counterparts of getAvailabilityFromRoom/getBookingFromRoom/getPricesFromRoom
    // above, backed by bb8-api's /bulk endpoints (AS-2587) rather than the bb_8_api SDK, which
    // doesn't have these operations generated yet.
    getBulkAvailability(context: any, opts: any) {
      return getAvailabilityBulk(opts.startDate, opts.endDate, opts.roomIds).catch((error: any) => {
        throw error instanceof Response ? new ApiError('300') : new ServerError('300');
      });
    },

    getBulkBookingsQuantity(context: any, opts: any) {
      return getBookingsQuantityBulk(opts.startDate, opts.endDate, opts.roomIds).catch((error: any) => {
        throw error instanceof Response ? new ApiError('400') : new ServerError('400');
      });
    },

    getBulkPrices(context: any, opts: any) {
      return getPricesBulk(opts.startDate, opts.endDate, opts.roomIds).catch((error: any) => {
        throw error instanceof Response ? new ApiError('510') : new ServerError('510');
      });
    },

    bulkUpdate({ commit }: any, opts: any) {
      return new Promise((resolve, reject) => {
        if (opts.availabilityQty && typeof (opts.availabilityQty) !== 'number') {
          opts.availabilityQty = parseInt(opts.availabilityQty, 10);
        }
        const payload = {
          roomId: opts.roomId,
          availabilityType: opts.availabilityType,
          availabilityQty: opts.availabilityQty,
        };
        AvailabilityApi.availabilityBulkUpdateAvailabilityCollection(
          opts.startDate,
          opts.endDate,
          opts.daysOfWeek,
          payload,
          (error: Error, resData: any, response: any) => {
            if (!error && typeof(response) !== 'undefined') {
              commit('updateAvailabilityDetail', {detail: response.body, roomId: opts.roomId});
              resolve();
            } else {
              if (typeof(response) !== 'undefined') {
                const errorMap: any = {
                  400: '311',
                  404: '312',
                };
                reject(new ApiError(errorMap[response.status] || '310'));
              } else {
                reject(new ServerError('310'));
              }
            }
          });
      });
    },

    bulkPriceUpdate({ commit }: any, opts: any) {
      const payload = {
        roomId: opts.roomId,
        amount: opts.amount,
        updateVariablePricingOnly: opts.updateVariablePricingOnly,
      };
      return new Promise((resolve, reject) => {
        PriceApi.priceBulkUpdatePriceCollection(opts.startDate, opts.endDate, opts.daysOfWeek, opts,
          (error: Error, resData: any, response: any) => {
          if (!error && typeof(response) !== 'undefined') {
            commit('updatePriceDetail', {detail: response.body, roomId: opts.roomId});
            resolve();
          } else {
            handleErrorResponse(error, () => {
              reject(response);
            });
          }
        });
      });
    },
  },

  getters: {
    isExtendedView: (state: any) => moment(state.dateRange.end).diff(moment(state.dateRange.start), 'days') > 19,
    isSmallView: (state: any) => state.windowWidth < 1200,
    isMobileView: (state: any) => state.windowWidth < 450,
    isBulkUpdateEnabled: (state: any) => {
      return process.env.VUE_APP_BULK_UPDATE_ENABLED !== '0' && state.windowWidth > 450 || false;
    },

    /**
     * returns how many days could fit in the window, accounting for the width of the caption column
     */
    maxDaysCanBeDisplayed(state: any): number {
      return Math.floor((state.windowWidth - UI_CAPTION_COLUMN_WIDTH) / UI_DAY_CELL_WIDTH);
    },

    /**
     * on UI load:
     * - if the screen is large enough, display 20 days
     * - if the screen is too small, only display what can fit in the screen
     */
    initialDaysToDisplay(state: any, getters: any): number {
      const DAYS_THAT_COULD_BE_DISPLAYED = getters.maxDaysCanBeDisplayed;
      return DAYS_THAT_COULD_BE_DISPLAYED > UI_DEFAULT_DAYS_VIEW - 1 ?
        UI_DEFAULT_DAYS_VIEW - 1 : DAYS_THAT_COULD_BE_DISPLAYED;
    },

    /**
     * returns:
     *  • 31 days is screen is large enough
     *  • only the number of days that can fit in the screen if less than 20
     *  • otherwise, 20 (default view)
     */
    maxDays(state: any, getters: any): number {
      const DAYS_THAT_COULD_BE_DISPLAYED = getters.maxDaysCanBeDisplayed;
      if (DAYS_THAT_COULD_BE_DISPLAYED > UI_EXTENDED_MAX_DAYS_VIEW - 1) {
        // display 31 days if the screen is large enough
        return UI_EXTENDED_MAX_DAYS_VIEW - 1;
      } else if (DAYS_THAT_COULD_BE_DISPLAYED < UI_DEFAULT_DAYS_VIEW - 1) {
        // display only the number of days that can fit in the screen if less than 20
        return DAYS_THAT_COULD_BE_DISPLAYED;
      } else {
        // otherwise, display 20 days (default view)
        return UI_DEFAULT_DAYS_VIEW - 1;
      }
    },

    roomsNeedOnboardAvailability: (state: any): any =>
      (state.roomList.filter((room: any) => (!room.hasAvailabilityProvided))),
    roomsNeedOnboardPrice: (state: any): any =>
      (state.roomList.filter((room: any) => (room.hasPriceProvided === false && room.pricingEnabled))),
    shouldShowOnboard: (state: any): boolean => {
      if (process.env.VUE_APP_ONBOARDING_ENABLED === '0') {
        return false;
      }
      const roomsNeedOnboardPrice =
        (state.roomList.filter((room: any) => (room.hasPriceProvided === false && room.pricingEnabled)));
      const roomsNeedOnboardAvailability = (state.roomList.filter((room: any) => (!room.hasAvailabilityProvided)));
      const onboardAvailability: boolean = Boolean(!state.isOnRequest
        && (Array.isArray(roomsNeedOnboardAvailability) && roomsNeedOnboardAvailability.length));
      const onboardPricing: boolean = Boolean(Array.isArray(roomsNeedOnboardPrice) && roomsNeedOnboardPrice.length);
      return onboardAvailability || onboardPricing;
    },
  },
};
